import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../authContext";
import {
  BusinessSetup,
  Tag,
  applyFormFilled,
  applyStaffNotes,
  foldChats,
  leadColumns,
  leadState,
  applySavedLeadState,
  loadSetup,
  businessId,
  numberInText,
  phoneDigits,
  primaryKey,
  tenDigitMobile,
  mobileDisplay,
  saveChannelId,
  saveSetup,
} from "./businessSetup";
import { getBusinessLeads, getLeadPipeline } from "../../../api/leads.service";
import { getInstagramMessageUsers } from "../../../api/instagram.service";
import { ChatRow, ago, leadsToChats, loadAccount, personName, scoreTone } from "./chats";
import CreateLeadDialog from "./CreateLeadDialog";

export default function CrmPage() {
  const navigate = useNavigate();
  const { displayPhoneNumber } = useAuth();
  const [setup, setSetup] = useState<BusinessSetup>(() => loadSetup());
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [stageIds, setStageIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [fieldFilters, setFieldFilters] = useState<Record<string, string[]>>({});
  const [loadError, setLoadError] = useState("");
  const [view, setView] = useState<"rows" | "board">("rows");
  const [draggingKey, setDraggingKey] = useState("");
  const [overStage, setOverStage] = useState("");
  const [moveError, setMoveError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [businessPhone, setBusinessPhone] = useState("");
  const [instagramBusinessId, setInstagramBusinessId] = useState("");
  const [igNames, setIgNames] = useState<Record<string, string>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const skipClick = useRef(false);

  const persist = (next: BusinessSetup) => {
    saveSetup(next);
    setSetup(next);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const account = await loadAccount(displayPhoneNumber);
        if (!cancelled) {
          setBusinessPhone(account.businessPhone);
          setInstagramBusinessId(account.instagramId);
        }
        const [leads, igUsers] = await Promise.all([
          getBusinessLeads({
            whatsappBusinessId: account.businessPhone,
            instagramBusinessId: account.instagramId,
          }),
          getInstagramMessageUsers(account.instagramId).catch(() => []),
        ]);
        if (!cancelled) {
          const names: Record<string, string> = {};
          igUsers.forEach((user) => {
            const name = personName(user.name || "") || personName(user.customerName || "") || personName(user.username || "");
            if (name && user.customerId) names[user.customerId] = name;
          });
          setIgNames(names);
        }
        const remote = await getLeadPipeline(businessId()).catch((): null => null);
        if (!cancelled) {
          const loaded = leadsToChats(leads);
          setChats(loaded);
          setSetup((current) => {
            const withPipeline = remote
              ? { ...current, stages: remote.stages, tags: remote.tags, fields: remote.fields }
              : current;
            const folded = foldChats(withPipeline, loaded);
            const byConversation = new Map(leads.map((lead) => [lead.conversationId, lead]));
            const withSaved = applySavedLeadState(folded, loaded.map((chat) => {
              const saved = byConversation.get(chat.key.replace(/^lead:/, ""));
              return { key: chat.key, stageId: saved?.stageId, tagIds: saved?.tagIds };
            }));
            const next = applyStaffNotes(applyFormFilled(withSaved, loaded), loaded);
            if (next !== current) saveSetup(next);
            return next;
          });
        }
      } catch {
        if (!cancelled) {
          setChats([]);
          setLoadError("Could not load leads.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [displayPhoneNumber, reloadKey]);

  const needle = query.trim().toLowerCase();
  const matching = chats.filter((chat, index, list) => {
    if (!`${chat.name} ${chat.handle}`.toLowerCase().includes(needle)) return false;
    const id = primaryKey(setup, chat.key);
    return list.findIndex((other) => primaryKey(setup, other.key) === id) === index;
  });
  const fieldChoices = setup.fields.map((field) => {
    const values = new Set<string>();
    chats.forEach((chat) => {
      const value = (leadState(setup, chat.key).attributes[field.id] || "").trim();
      if (value) values.add(value);
    });
    return { field, values: Array.from(values).sort((a, b) => a.localeCompare(b)) };
  }).filter((choice) => choice.values.length > 0);
  const passesFilters = (chat: ChatRow, stageToo: boolean) => {
    const lead = leadState(setup, chat.key);
    const fieldsMatch = Object.entries(fieldFilters).every(([fieldId, picked]) => {
      if (picked.length === 0) return true;
      return picked.includes((lead.attributes[fieldId] || "").trim());
    });
    if (!fieldsMatch) return false;
    if (tagIds.length > 0 && !tagIds.some((id) => lead.tagIds.includes(id))) return false;
    if (stageToo && stageIds.length > 0 && !stageIds.includes(lead.stageId)) return false;
    return true;
  };
  const inFilters = matching.filter((chat) => passesFilters(chat, false));
  const shown = inFilters.filter((chat) => passesFilters(chat, true));
  const fieldFilterOn = Object.values(fieldFilters).some((picked) => picked.length > 0);
  const filtersOn = fieldFilterOn || tagIds.length > 0 || stageIds.length > 0;
  const toggleId = (current: string[], id: string) => (
    current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
  );

  const reminders = chats
    .map((chat) => ({ chat, reminder: leadState(setup, chat.key).reminder }))
    .filter((item) => item.reminder);

  const moveToStage = (key: string, stageId: string) => {
    const lead = leadState(setup, key);
    if (lead.stageId === stageId) return;
    const nextStage = setup.stages.find((stage) => stage.id === stageId);
    if (nextStage?.requireAttribute && nextStage.attributeName) {
      const filled = (lead.attributes[nextStage.attributeName] || "").trim();
      if (!filled) {
        setMoveError(`Fill in ${nextStage.attributeName} before this lead can move to ${nextStage.name}.`);
        return;
      }
    }
    setMoveError("");
    const id = primaryKey(setup, key);
    persist({
      ...setup,
      leads: { ...setup.leads, [id]: { ...lead, stageId } },
    });
  };

  const setLeadTags = (key: string, tagIds: string[]) => {
    const id = primaryKey(setup, key);
    const lead = leadState(setup, key);
    persist({
      ...setup,
      leads: { ...setup.leads, [id]: { ...lead, tagIds } },
    });
  };

  const setLeadField = (key: string, fieldId: string, value: string) => {
    const id = primaryKey(setup, key);
    const lead = leadState(setup, key);
    persist({
      ...setup,
      leads: {
        ...setup.leads,
        [id]: { ...lead, attributes: { ...lead.attributes, [fieldId]: value } },
      },
    });
  };

  const columns = leadColumns(setup);
  const visibleColumns = columns.filter((column) => column.visible);
  const saveColumns = (next: typeof columns) => {
    persist({
      ...setup,
      columns: next.map((column) => ({ id: column.id, width: column.width, visible: column.visible })),
    });
  };
  const toggleColumn = (id: string) => {
    if (id === "lead") return;
    saveColumns(columns.map((column) => (column.id === id ? { ...column, visible: !column.visible } : column)));
  };
  const resizeColumn = (id: string, startX: number, startWidth: number) => {
    const move = (event: MouseEvent) => {
      const width = Math.max(90, startWidth + event.clientX - startX);
      saveColumns(leadColumns(loadSetup()).map((column) => (column.id === id ? { ...column, width } : column)));
    };
    const stop = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
  };

  return (
    <div className="relative pb-24">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Leads</h2>
          <p className="text-sm text-gray-500">People saved for this business. Open a person to see their messages.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setCreateOpen(true)} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white">
            Create
          </button>
        </div>
      </div>
      <div className="mb-4 flex gap-1">
        {(["rows", "board"] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${view === id ? "bg-blue-600 text-white" : "bg-white text-gray-600"}`}
          >
            {id === "rows" ? "Rows" : "Board"}
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search Leads"
          className="w-full max-w-md rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <div className="relative">
          <button
            type="button"
            onClick={() => setColumnsOpen((open) => !open)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700"
          >
            Columns
          </button>
          {columnsOpen ? (
            <div className="absolute left-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
              {columns.map((column) => (
                <label key={column.id} className="flex items-center gap-2 px-2 py-1 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={column.visible}
                    disabled={column.id === "lead"}
                    onChange={() => toggleColumn(column.id)}
                  />
                  {column.label}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {fieldChoices.map(({ field, values }) => {
        const picked = fieldFilters[field.id] || [];
        return (
          <div key={field.id} className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{field.name}</span>
            {values.map((value) => {
              const on = picked.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFieldFilters({ ...fieldFilters, [field.id]: toggleId(picked, value) })}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    on ? "bg-blue-600 text-white" : picked.length ? "bg-white text-gray-400" : "bg-white text-gray-600"
                  }`}
                >
                  {value}
                </button>
              );
            })}
          </div>
        );
      })}
      {filtersOn ? (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => {
              setFieldFilters({});
              setTagIds([]);
              setStageIds([]);
            }}
            className="text-xs font-medium text-blue-600"
          >
            Clear filters
          </button>
        </div>
      ) : null}
      {setup.tags.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Tags</span>
          {setup.tags.map((tag) => {
            const on = tagIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => setTagIds(toggleId(tagIds, tag.id))}
                className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${on ? "ring-2 ring-gray-900 ring-offset-1" : tagIds.length ? "opacity-50" : ""}`}
                style={{ background: tag.color }}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      ) : null}

      {setup.stages.length === 0 ? (
        <p className="mb-4 text-sm text-gray-500">No stages yet. Add them in Settings for this business.</p>
      ) : (
        <div className="mb-4 flex gap-1 overflow-x-auto">
          {setup.stages.map((stage) => {
            const on = stageIds.includes(stage.id);
            const count = inFilters.filter((chat) => leadState(setup, chat.key).stageId === stage.id).length;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => setStageIds(toggleId(stageIds, stage.id))}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-xs font-semibold text-white ${on ? "ring-2 ring-gray-900 ring-offset-1" : stageIds.length ? "opacity-50" : ""}`}
                style={{ background: stage.color }}
              >
                {count} {stage.name}
              </button>
            );
          })}
        </div>
      )}

      {loadError ? <p className="mb-3 text-sm text-red-600">{loadError}</p> : null}
      {moveError ? <p className="mb-3 text-sm text-red-600">{moveError}</p> : null}
      {loading ? <p className="text-sm text-gray-500">Loading leads...</p> : null}

      {view === "rows" ? (
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="table-fixed text-left text-sm" style={{ width: visibleColumns.reduce((sum, column) => sum + column.width, 0) }}>
          <thead className="border-b bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {visibleColumns.map((column) => (
                <th key={column.id} className="relative px-3 py-2 font-medium" style={{ width: column.width }}>
                  {column.label}
                  <span
                    role="separator"
                    aria-label={`Resize ${column.label}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      resizeColumn(column.id, event.clientX, column.width);
                    }}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && !loading ? (
              <tr>
                <td colSpan={Math.max(visibleColumns.length, 1)} className="px-3 py-6 text-gray-500">
                  {filtersOn || needle ? "No leads match these filters." : "No leads yet. Create one, or a person appears here when they message."}
                </td>
              </tr>
            ) : null}
            {shown.map((chat) => {
              const lead = leadState(setup, chat.key);
              const stage = setup.stages.find((item) => item.id === lead.stageId);
              const spotted = numberInText(chat.preview || "");
              const auto = Boolean(spotted) && phoneDigits(lead.whatsappNumber) === spotted;
              const cell = (id: string) => {
                if (id === "lead") {
                  return (
                    <button
                      type="button"
                      className="text-left"
                      onClick={() => navigate(`/events/conversations?lead=${encodeURIComponent(chat.key)}&channel=${chat.channel}`)}
                    >
                      <span className="flex items-center gap-2 font-medium text-gray-900">{chat.name}</span>
                      <span className="text-xs text-gray-400">{ago(chat.at)}</span>
                    </button>
                  );
                }
                if (id === "score") {
                  return (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${scoreTone(chat.score)}`}>{chat.score ?? 0}</span>
                  );
                }
                if (id === "stage") {
                  return (
                    <span className="rounded-full px-2 py-1 text-xs font-medium text-white" style={{ background: stage?.color || "#94a3b8" }}>
                      {stage?.name || "No stage"}
                    </span>
                  );
                }
                if (id === "tags") {
                  return (
                    <TagPicker
                      tags={setup.tags}
                      selected={lead.tagIds}
                      onChange={(tagIds) => setLeadTags(chat.key, tagIds)}
                    />
                  );
                }
                if (id === "instagram") return instagramLabel(lead.instagramId || chat.instagramId || "", chat.customerId || "", chat.channel, chat.name, igNames);
                if (id === "whatsapp") {
                  return (
                    <>
                      <ClickToEdit
                        value={mobileDisplay(lead.whatsappNumber)}
                        placeholder="Add WhatsApp number"
                        label={`WhatsApp number for ${chat.name}`}
                        check={(value) => tenDigitMobile(value).error}
                        onSave={(value) => persist(saveChannelId(setup, chat.key, "whatsappNumber", value))}
                      />
                      {auto ? <span className="mt-1 block text-xs text-green-700">Connected from the chat</span> : null}
                    </>
                  );
                }
                if (id === "phone") {
                  return (
                    <ClickToEdit
                      value={mobileDisplay(lead.phoneId)}
                      placeholder="Add phone number"
                      label={`Phone for ${chat.name}`}
                      check={(value) => tenDigitMobile(value).error}
                      onSave={(value) => persist(saveChannelId(setup, chat.key, "phoneId", value))}
                    />
                  );
                }
                const field = setup.fields.find((item) => item.id === id);
                if (!field) return null;
                return (
                  <ClickToEdit
                    value={lead.attributes[field.id] || ""}
                    placeholder={`Add ${field.name}`}
                    label={`${field.name} for ${chat.name}`}
                    onSave={(value) => setLeadField(chat.key, field.id, value)}
                  />
                );
              };
              return (
                <tr key={primaryKey(setup, chat.key)} className="border-b last:border-0">
                  {visibleColumns.map((column) => (
                    <td key={column.id} className="overflow-hidden px-3 py-3" style={{ width: column.width }}>
                      {cell(column.id)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      ) : (
      <div className="flex gap-3 overflow-x-auto pb-2">
        {setup.stages.filter((stage) => stageIds.length === 0 || stageIds.includes(stage.id)).map((stage) => {
          const cards = shown.filter((chat) => leadState(setup, chat.key).stageId === stage.id);
          const activeDrop = overStage === stage.id && draggingKey;
          return (
            <div
              key={stage.id}
              role="region"
              aria-label={`${stage.name} column`}
              onDragOver={(event) => {
                event.preventDefault();
                setOverStage(stage.id);
              }}
              onDragLeave={() => setOverStage((current) => (current === stage.id ? "" : current))}
              onDrop={(event) => {
                event.preventDefault();
                const key = event.dataTransfer.getData("text/plain") || draggingKey;
                if (key) moveToStage(key, stage.id);
                setDraggingKey("");
                setOverStage("");
              }}
              className={`flex w-72 shrink-0 flex-col rounded-xl bg-gray-100 ${activeDrop ? "ring-2 ring-blue-500" : ""}`}
            >
              <div className="flex items-center gap-2 px-3 py-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color }} />
                <span className="text-sm font-semibold text-gray-900">{stage.name}</span>
                <span className="text-xs text-gray-500">{cards.length}</span>
              </div>
              <div className="min-h-[240px] space-y-2 px-2 pb-3">
                {cards.map((chat) => {
                  const lead = leadState(setup, chat.key);
                  return (
                    <div
                      key={primaryKey(setup, chat.key)}
                      draggable
                      role="button"
                      tabIndex={0}
                      aria-label={`${chat.name} in ${stage.name}`}
                      onDragStart={(event) => {
                        skipClick.current = true;
                        event.dataTransfer.setData("text/plain", chat.key);
                        event.dataTransfer.effectAllowed = "move";
                        setDraggingKey(chat.key);
                      }}
                      onDragEnd={() => { setDraggingKey(""); setOverStage(""); }}
                      onClick={() => {
                        if (skipClick.current) {
                          skipClick.current = false;
                          return;
                        }
                        navigate(`/events/conversations?lead=${encodeURIComponent(chat.key)}&channel=${chat.channel}`);
                      }}
                      className="cursor-grab rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm active:cursor-grabbing"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        {chat.name}
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${scoreTone(chat.score)}`}>{chat.score ?? 0}</span>
                      </span>
                      {chat.handle ? <span className="mt-0.5 block text-xs text-gray-500">{chat.handle}</span> : null}
                      {setup.tags.some((tag) => lead.tagIds.includes(tag.id)) ? (
                        <span className="mt-2 flex flex-wrap gap-1">
                          {setup.tags.filter((tag) => lead.tagIds.includes(tag.id)).map((tag) => (
                            <span key={tag.id} className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white" style={{ background: tag.color }}>
                              {tag.name}
                            </span>
                          ))}
                        </span>
                      ) : null}
                      <span className="mt-1 block text-[11px] text-gray-400">{ago(chat.at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {reminders.length > 0 ? (
        <div className="fixed bottom-4 right-4 z-20 w-72 rounded-xl bg-gray-900 p-3 text-white shadow-xl">
          <p className="mb-2 text-sm font-semibold">Pending reminders</p>
          <ul className="space-y-2">
            {reminders.slice(0, 4).map(({ chat, reminder }) => (
              <li key={chat.key} className="text-xs">
                <button
                  type="button"
                  className="text-left"
                  onClick={() => navigate(`/events/conversations?lead=${encodeURIComponent(chat.key)}&channel=${chat.channel}`)}
                >
                  <span className="block font-medium">{chat.name}</span>
                  <span className="text-gray-300">{reminder ? new Date(reminder.dueAt).toLocaleString() : ""}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {createOpen ? (
        <CreateLeadDialog
          businessPhone={businessPhone}
          instagramBusinessId={instagramBusinessId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            setReloadKey((value) => value + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function instagramLabel(
  instagramId: string,
  customerId: string,
  channel: string,
  name: string,
  igNames: Record<string, string>
): string {
  const handle = instagramId.replace(/^@/, "").trim();
  const id = customerId || (/^\d+$/.test(handle) ? handle : "");
  const fromInstagram = id ? igNames[id] : "";
  if (fromInstagram) return fromInstagram;
  if (/^\d+$/.test(handle) || channel === "instagram" || (handle && !/^\d+$/.test(handle))) {
    const saved = personName(name);
    if (saved) return saved;
    if (handle && !/^\d+$/.test(handle)) return handle;
  }
  return "—";
}

function TagPicker({
  tags,
  selected,
  onChange,
}: {
  tags: Tag[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const chosen = tags.filter((tag) => selected.includes(tag.id));
  if (tags.length === 0) {
    return <span className="text-xs text-gray-400">No tags yet</span>;
  }
  return (
    <div className="relative flex flex-wrap items-center gap-1">
      {chosen.map((tag) => (
        <span key={tag.id} className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: tag.color }}>
          {tag.name}
        </span>
      ))}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-600"
      >
        {open ? "Close" : "Tag"}
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          {tags.map((tag) => {
            const on = selected.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-gray-50"
                onClick={() => onChange(on ? selected.filter((id) => id !== tag.id) : selected.concat(tag.id))}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: tag.color }} />
                <span className="truncate">{tag.name}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ClickToEdit({
  value,
  placeholder,
  label,
  check,
  onSave,
}: {
  value: string;
  placeholder: string;
  label: string;
  check?: (value: string) => string;
  onSave: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    setDraft(value);
    setError("");
    inputRef.current?.focus();
  }, [editing, value]);

  const save = () => {
    const problem = check?.(draft) || "";
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    onSave(draft.trim());
    setEditing(false);
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className={`text-sm ${value ? "text-gray-900" : "text-gray-400"}`}>{value || placeholder}</span>
        <button
          type="button"
          aria-label={`Edit ${label}`}
          onClick={() => setEditing(true)}
          className="text-gray-400 hover:text-blue-600"
        >
          <span aria-hidden="true">✎</span>
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={inputRef}
        value={draft}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") setEditing(false);
        }}
        className="w-36 border-b border-gray-300 bg-transparent py-0.5 text-sm text-gray-900 outline-none"
      />
      <button
        type="button"
        aria-label={`Save ${label}`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={save}
        className="text-blue-600 hover:text-blue-800"
      >
        <span aria-hidden="true">✓</span>
      </button>
      {error ? <span className="text-xs text-red-600">{error}</span> : null}
    </span>
  );
}

